import { motion } from 'framer-motion';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, AreaChart, Area, Legend,
} from 'recharts';
import { BarChart3, Activity, LineChart as LineChartIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { MODELS, MODEL_KEYS } from '../lib/models.js';
import { compareAcrossModels, fmtUSD, projectMonthly } from '../lib/estimate.js';

export function CostVisualizer({ task, maxTokens, runsPerDay, currentModelKeys, maxTier = 5 }) {
  const [view, setView] = useState('compare');

  const compared = useMemo(() => {
    const eligible = MODEL_KEYS.filter((k) => MODELS[k].tier <= maxTier);
    return compareAcrossModels({ task, maxTokens, modelKeys: eligible });
  }, [task, maxTokens, maxTier]);

  const projection = useMemo(() => {
    const days = Array.from({ length: 30 }, (_, i) => i + 1);
    const selected = currentModelKeys.length ? currentModelKeys : compared.slice(0, 3).map((c) => c.key);
    const perRun = selected.reduce((s, k) => {
      const row = compared.find((c) => c.key === k);
      return s + (row?.total || 0);
    }, 0);
    return days.map((d) => ({
      day: d,
      cost: perRun * runsPerDay * d,
    }));
  }, [compared, runsPerDay, currentModelKeys]);

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.2 }}
      className="panel-strong p-5"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="section-title">
          <BarChart3 className="h-4 w-4 text-accent-cyan" />
          Cost intelligence
        </div>
        <div className="flex gap-1 rounded-xl border border-border-subtle bg-bg-deep/60 p-1">
          {[
            { id: 'compare', icon: BarChart3, label: 'compare' },
            { id: 'projection', icon: LineChartIcon, label: 'projection' },
            { id: 'split', icon: Activity, label: 'split' },
          ].map((b) => (
            <button
              key={b.id}
              onClick={() => setView(b.id)}
              className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs transition ${view === b.id ? 'bg-accent-cyan/15 text-accent-cyan-light' : 'text-ink-dim hover:text-ink-primary'}`}
            >
              <b.icon className="h-3 w-3" />
              {b.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-72">
        {view === 'compare' && <CompareChart data={compared} highlight={currentModelKeys} />}
        {view === 'projection' && <ProjectionChart data={projection} runsPerDay={runsPerDay} />}
        {view === 'split' && <SplitChart data={compared} highlight={currentModelKeys} />}
      </div>

      {view === 'compare' && (
        <p className="mt-3 font-mono text-[10px] text-ink-dim">
          Estimated $/run for the current task across all eligible models. Highlighted = currently selected.
          Cheapest: <span className="text-accent-cyan-light">{compared[0]?.label}</span> · costliest: <span className="text-accent-orange-soft">{compared[compared.length - 1]?.label}</span>
        </p>
      )}
      {view === 'projection' && (
        <p className="mt-3 font-mono text-[10px] text-ink-dim">
          30-day cumulative cost at <span className="text-accent-orange-soft">{runsPerDay} runs/day</span> for the currently selected agent set.
        </p>
      )}
      {view === 'split' && (
        <p className="mt-3 font-mono text-[10px] text-ink-dim">
          Input vs output cost split — output dominates for verbose models.
        </p>
      )}
    </motion.section>
  );
}

function CompareChart({ data, highlight }) {
  const set = new Set(highlight);
  return (
    <ResponsiveContainer>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -12, bottom: 32 }}>
        <CartesianGrid stroke="#1e3a5f" strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={{ fill: '#94a8c4', fontSize: 10 }} angle={-30} textAnchor="end" height={50} interval={0} />
        <YAxis tick={{ fill: '#94a8c4', fontSize: 10 }} tickFormatter={(v) => fmtUSD(v)} />
        <Tooltip content={<CostTip />} cursor={{ fill: 'rgba(56,189,248,0.06)' }} />
        <Bar dataKey="total" radius={[6, 6, 0, 0]}>
          {data.map((d) => (
            <Cell
              key={d.key}
              fill={set.has(d.key) ? '#ff8c42' : d.color}
              fillOpacity={set.has(d.key) ? 1 : 0.55}
              stroke={set.has(d.key) ? '#ffb27a' : 'none'}
              strokeWidth={set.has(d.key) ? 1.5 : 0}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function ProjectionChart({ data }) {
  return (
    <ResponsiveContainer>
      <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
        <defs>
          <linearGradient id="costGrad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#ff8c42" stopOpacity={0.55} />
            <stop offset="100%" stopColor="#ff8c42" stopOpacity={0.05} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke="#1e3a5f" strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="day" tick={{ fill: '#94a8c4', fontSize: 10 }} />
        <YAxis tick={{ fill: '#94a8c4', fontSize: 10 }} tickFormatter={(v) => fmtUSD(v)} />
        <Tooltip
          contentStyle={{ background: '#0a1628', border: '1px solid #2c4d80', borderRadius: 12 }}
          labelStyle={{ color: '#94a8c4' }}
          formatter={(v) => [fmtUSD(v), 'cumulative']}
          labelFormatter={(d) => `day ${d}`}
        />
        <Area type="monotone" dataKey="cost" stroke="#ff8c42" strokeWidth={2} fill="url(#costGrad)" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function SplitChart({ data, highlight }) {
  const subset = highlight.length
    ? data.filter((d) => highlight.includes(d.key))
    : data.slice(0, 6);
  return (
    <ResponsiveContainer>
      <BarChart data={subset} margin={{ top: 8, right: 8, left: -12, bottom: 32 }}>
        <CartesianGrid stroke="#1e3a5f" strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="label" tick={{ fill: '#94a8c4', fontSize: 10 }} angle={-25} textAnchor="end" height={50} interval={0} />
        <YAxis tick={{ fill: '#94a8c4', fontSize: 10 }} tickFormatter={(v) => fmtUSD(v)} />
        <Tooltip content={<CostTip split />} cursor={{ fill: 'rgba(56,189,248,0.06)' }} />
        <Legend wrapperStyle={{ fontSize: 11, color: '#94a8c4' }} />
        <Bar dataKey="input" stackId="a" fill="#38bdf8" radius={[0, 0, 0, 0]} />
        <Bar dataKey="output" stackId="a" fill="#ff8c42" radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function CostTip({ active, payload, split }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-xl border border-border-strong bg-bg-deep/95 p-3 text-xs shadow-glow-cyan">
      <div className="mb-1 flex items-center gap-2 font-medium text-ink-primary">
        <span className="h-2 w-2 rounded-full" style={{ background: d.color }} />
        {d.label}
      </div>
      <div className="font-mono text-ink-muted">tier {d.tier} · {d.vendor}</div>
      <div className="mt-1.5 space-y-0.5 font-mono">
        {split ? (
          <>
            <div className="text-accent-cyan-light">in: {fmtUSD(d.input)}</div>
            <div className="text-accent-orange-soft">out: {fmtUSD(d.output)}</div>
            <div className="text-ink-primary">total: {fmtUSD(d.total)}</div>
          </>
        ) : (
          <div className="text-ink-primary">{fmtUSD(d.total)} / run</div>
        )}
      </div>
    </div>
  );
}
